import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';
import './theme/global.css';
import './theme/datagrid.css';
import './theme/toggle.css';

import './i18n/config';
import { mantineTheme } from './theme/mantine';
import { router } from './router';
import { restoreSession } from './api/auth';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { IdleWarning } from '@/components/IdleWarning';
import { startSessionWatch } from '@/lib/session';
import { useAuth } from './store/auth';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Boot sequence:
 *  - if we have a persisted refresh token, try to restore the session
 *    via /v1/auth/refresh BEFORE rendering the router. That way the
 *    auth guards on protected routes never bounce a returning user
 *    through the login page on a hard reload.
 *  - otherwise render straight away and let the user reach /login.
 */
function Boot() {
  const refreshToken = useAuth((s) => s.refreshToken);
  const [restored, setRestored] = useState(refreshToken == null);

  useEffect(() => {
    // Surveillance de la session : activité, renouvellement anticipé,
    // déconnexion après inactivité. Montée une fois, pour toute la durée de
    // vie de l'application.
    startSessionWatch();
    if (refreshToken == null) return;
    void restoreSession().finally(() => setRestored(true));
    // We only want this to run on initial mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!restored) return null;

  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
      <IdleWarning />
    </ErrorBoundary>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <MantineProvider theme={mantineTheme} defaultColorScheme="light">
      <ModalsProvider>
        <Notifications position="top-right" />
        <QueryClientProvider client={queryClient}>
          <Boot />
        </QueryClientProvider>
      </ModalsProvider>
    </MantineProvider>
  </React.StrictMode>,
);
